/**
 * usePwaUpdate
 * ============================================================
 * Composable que detecta cuando hay una nueva versión del
 * Service Worker disponible y expone el control para aplicarla.
 *
 * FUNCIONAMIENTO:
 *  1. VitePWA con registerType: 'prompt' registra el SW pero NO
 *     lo activa automáticamente. Emite el evento `needRefresh`
 *     cuando detecta un SW nuevo esperando.
 *  2. Este composable captura ese evento y muestra un banner
 *     al usuario. Si el usuario acepta, activa el worker y este composable
 *     recarga de forma explícita al cambiar el controlador (con timeout).
 *  3. Si el usuario ignora el banner, el SW nuevo sigue esperando
 *     hasta el próximo cierre/apertura de la pestaña.
 *
 * USO en App.vue o el componente raíz:
 *   import { usePwaUpdate } from '@/composables/usePwaUpdate'
 *   const { needsUpdate, updateNow, dismissUpdate } = usePwaUpdate()
 * ============================================================
 */

import { ref, onMounted, onBeforeUnmount } from 'vue'
import { useRegisterSW } from 'virtual:pwa-register/vue'
import { applyServiceWorkerUpdate } from '@/utils/pwaUpdate'

export function usePwaUpdate() {
  // ¿Hay actualización disponible?
  const needsUpdate = ref(false)

  // ¿La actualización está siendo aplicada (loading)?
  const isUpdating = ref(false)

  // Función que VitePWA expone para activar el nuevo SW
  let _updateServiceWorker = null

  // Intervalo de comprobación periódica (en ms)
  // Para un reproductor siempre encendido, verificar cada 60 minutos
  const CHECK_INTERVAL_MS = 60 * 60 * 1000

  const {
    needRefresh,         // Ref<boolean> de VitePWA
    offlineReady,        // Ref<boolean> — app ya funciona offline
    updateServiceWorker  // función para activar el nuevo SW
  } = useRegisterSW({
    immediate: true,

    onRegistered(registration) {
      if (!registration) return
      

      // Forzar una comprobación periódica de actualizaciones.
      // El browser solo comprueba el SW al navegar; en una app tipo
      // kiosk/reproductor que nunca se cierra esto no ocurre naturalmente.
      const intervalId = setInterval(() => {
        
        registration.update().catch(err => {
          console.warn('[PWA] Error al comprobar actualización:', err)
        })
      }, CHECK_INTERVAL_MS)

      // Guardar el id para limpiarlo si el componente se desmonta
      _checkIntervalId = intervalId
    },

    onNeedRefresh() {
      console.log('🔔 [PWA] Nueva versión disponible')

      // Intentar obtener el usuario logueado
      try {
        const userReprod = localStorage.getItem('userReprod')
        if (userReprod) {
          const user = JSON.parse(userReprod)
          const userName = user.nombre || user.usuario || 'Usuario'
          console.log(`👤 [PWA] Usuario actual: ${userName}`)
        }
      } catch (e) {
        // Silencioso si no se puede obtener
      }

      needsUpdate.value = true
    },

    onOfflineReady() {
      //('[PWA] App lista para funcionar offline')
    },

    onRegisterError(error) {
      console.error('[PWA] Error al registrar Service Worker:', error)
    }
  })

  _updateServiceWorker = updateServiceWorker
  let _checkIntervalId = null

  /**
   * Aplica la actualización inmediatamente.
   * Guarda el estado del reproductor antes de recargar
   * para restaurarlo después.
   */
  const updateNow = async () => {
    if (!_updateServiceWorker) return
    isUpdating.value = true

    // Obtener usuario para logs
    let userName = 'Usuario'
    try {
      const userReprod = localStorage.getItem('userReprod')
      if (userReprod) {
        const user = JSON.parse(userReprod)
        userName = user.nombre || user.usuario || 'Usuario'
      }
    } catch (e) {
      // Silencioso
    }

    try {
      console.log(`🔄 [${userName}] Iniciando actualización PWA...`)

      // Guardar estado del reproductor antes de actualizar
      try {
        const playerState = {
          timestamp: new Date().toISOString(),
          wasPlaying: true, // Indicador para restaurar después
          updateReason: 'pwa_update',
          user: userName
        }
        localStorage.setItem('pwa_update_in_progress', JSON.stringify(playerState))
        console.log(`💾 [${userName}] Estado del reproductor guardado antes de actualizar`)
      } catch (e) {
        console.warn(`[${userName}] No se pudo guardar el estado:`, e)
      }

      await applyServiceWorkerUpdate({
        updateServiceWorker: _updateServiceWorker,
        serviceWorker: navigator.serviceWorker,
        reload: () => window.location.reload()
      })
    } catch (err) {
      console.error(`❌ [${userName}] Error al aplicar actualización:`, err)
      isUpdating.value = false
      // Limpiar flag si falló
      localStorage.removeItem('pwa_update_in_progress')
    }
  }

  /**
   * Descarta el aviso sin aplicar la actualización.
   * El nuevo SW sigue esperando; se activará en el próximo
   * cierre/apertura de pestaña de forma natural.
   */
  const dismissUpdate = () => {
    needsUpdate.value = false
  }

  onBeforeUnmount(() => {
    if (_checkIntervalId) {
      clearInterval(_checkIntervalId)
      _checkIntervalId = null
    }
  })

  return {
    needsUpdate,
    offlineReady,
    isUpdating,
    updateNow,
    dismissUpdate
  }
}
